import React, { useEffect, useState, createContext, useContext } from 'react';
export interface User {
  id: string;
  name: string;
  avatar: string;
  totalCalls: number;
  averageScore: number;
  callHistory: CallRecord[];
  topCzarWins: number;
  lastLogin: string;
  topRepRank?: 1 | 2 | 3 | null;
}
export interface CallRecord {
  id: string;
  date: string;
  duration: string;
  score: number;
  sentiment: 'positive' | 'neutral' | 'negative';
  notes: string;
}
interface UserContextType {
  currentUser: User | null;
  login: (userId: string) => void;
  logout: () => void;
  updateUser: (user: User) => void;
}
const UserContext = createContext<UserContextType | undefined>(undefined);
export function UserProvider({
  children
}: {
  children: ReactNode;
}) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  // Load user from localStorage on mount
  useEffect(() => {
    const savedUserId = localStorage.getItem('currentUserId');
    if (savedUserId) {
      const users = getUsersData();
      const user = users.find(u => u.id === savedUserId);
      if (user) {
        setCurrentUser(user);
      }
    }
  }, []);
  const login = (userId: string) => {
    const users = getUsersData();
    const user = users.find(u => u.id === userId);
    if (user) {
      const updatedUser = {
        ...user,
        lastLogin: new Date().toISOString()
      };
      setCurrentUser(updatedUser);
      localStorage.setItem('currentUserId', userId);
      updateUserData(updatedUser);
    }
  };
  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('currentUserId');
  };
  const updateUser = (user: User) => {
    setCurrentUser(user);
    updateUserData(user);
  };
  return <UserContext.Provider value={{
    currentUser,
    login,
    logout,
    updateUser
  }} data-id="element-604">
      {children}
    </UserContext.Provider>;
}
export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}
// Helper functions to manage user data in localStorage
function getUsersData(): User[] {
  const stored = localStorage.getItem('usersData');
  if (stored) {
    return JSON.parse(stored);
  }
  // Initialize with default data if not exists
  const defaultUsers = getDefaultUsers();
  localStorage.setItem('usersData', JSON.stringify(defaultUsers));
  return defaultUsers;
}
function updateUserData(user: User) {
  const users = getUsersData();
  const index = users.findIndex(u => u.id === user.id);
  if (index !== -1) {
    users[index] = user;
    localStorage.setItem('usersData', JSON.stringify(users));
  }
}
function getDefaultUsers(): User[] {
  return [{
    id: 'drew',
    name: 'Drew',
    avatar: 'https://get.clientczar.com/wp-content/uploads/2025/12/Drew.png',
    totalCalls: 0,
    averageScore: 0,
    callHistory: [],
    topCzarWins: 0,
    lastLogin: '',
    topRepRank: null
  }, {
    id: 'jack',
    name: 'Jack',
    avatar: 'https://get.clientczar.com/wp-content/uploads/2025/12/Jack.png',
    totalCalls: 1,
    averageScore: 19,
    callHistory: [{
      id: '1',
      date: '2025-12-05T14:00:00',
      duration: '1:49',
      score: 19,
      sentiment: 'positive',
      notes: 'Successful'
    }],
    topCzarWins: 0,
    lastLogin: '',
    topRepRank: null
  }, {
    id: 'justin',
    name: 'Justin',
    avatar: 'https://get.clientczar.com/wp-content/uploads/2025/12/Justin-1.png',
    totalCalls: 0,
    averageScore: 0,
    callHistory: [],
    topCzarWins: 0,
    lastLogin: '',
    topRepRank: null
  }, {
    id: 'luke',
    name: 'Luke',
    avatar: 'https://get.clientczar.com/wp-content/uploads/2025/12/Luke.png',
    totalCalls: 25,
    averageScore: 530,
    callHistory: [{
      id: '1',
      date: '2025-12-06T02:56:00',
      duration: '0:16',
      score: 3,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '2',
      date: '2025-12-06T00:02:00',
      duration: '0:48',
      score: 7,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '3',
      date: '2025-12-05T22:19:00',
      duration: '1:19',
      score: 5,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '4',
      date: '2025-12-05T17:32:00',
      duration: '0:30',
      score: 3,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '5',
      date: '2025-12-05T16:24:00',
      duration: '3:20',
      score: 17,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '6',
      date: '2025-12-05T14:38:00',
      duration: '6:08',
      score: 28,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '7',
      date: '2025-12-05T14:29:00',
      duration: '5:13',
      score: 37,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '8',
      date: '2025-12-05T14:03:00',
      duration: '26:18',
      score: 107,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '9',
      date: '2025-12-05T14:02:00',
      duration: '0:30',
      score: 4,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '10',
      date: '2025-12-05T14:00:00',
      duration: '1:49',
      score: 19,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '11',
      date: '2025-12-05T14:00:00',
      duration: '1:38',
      score: 10,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '12',
      date: '2025-12-05T13:58:00',
      duration: '0:18',
      score: 3,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '13',
      date: '2025-12-05T13:39:00',
      duration: '16:45',
      score: 74,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '14',
      date: '2025-12-05T13:38:00',
      duration: '1:27',
      score: 8,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '15',
      date: '2025-12-05T13:20:00',
      duration: '20:19',
      score: 74,
      sentiment: 'negative',
      notes: 'Error'
    }, {
      id: '16',
      date: '2025-12-05T13:19:00',
      duration: '0:28',
      score: 4,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '17',
      date: '2025-12-05T13:19:00',
      duration: '0:12',
      score: 3,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '18',
      date: '2025-12-05T13:01:00',
      duration: '0:10',
      score: 1,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '19',
      date: '2025-12-05T12:45:00',
      duration: '26:07',
      score: 91,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '20',
      date: '2025-12-05T12:44:00',
      duration: '0:03',
      score: 1,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '21',
      date: '2025-12-05T12:43:00',
      duration: '0:54',
      score: 7,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '22',
      date: '2025-12-05T12:36:00',
      duration: '4:10',
      score: 13,
      sentiment: 'negative',
      notes: 'Error'
    }, {
      id: '23',
      date: '2025-12-05T12:35:00',
      duration: '0:30',
      score: 3,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '24',
      date: '2025-12-05T11:37:00',
      duration: '0:30',
      score: 3,
      sentiment: 'positive',
      notes: 'Successful'
    }, {
      id: '25',
      date: '2025-12-05T11:29:00',
      duration: '1:44',
      score: 12,
      sentiment: 'positive',
      notes: 'Successful'
    }],
    topCzarWins: 0,
    lastLogin: '',
    topRepRank: 1
  }, {
    id: 'nick',
    name: 'Nick',
    avatar: 'https://get.clientczar.com/wp-content/uploads/2025/12/Nick.png',
    totalCalls: 0,
    averageScore: 0,
    callHistory: [],
    topCzarWins: 0,
    lastLogin: '',
    topRepRank: null
  }, {
    id: 'zach',
    name: 'Zach',
    avatar: 'https://get.clientczar.com/wp-content/uploads/2025/12/Zach.png',
    totalCalls: 1,
    averageScore: 6,
    callHistory: [{
      id: '1',
      date: '2025-12-04T23:01:00',
      duration: '0:51',
      score: 6,
      sentiment: 'positive',
      notes: 'Successful'
    }],
    topCzarWins: 0,
    lastLogin: '',
    topRepRank: null
  }];
}